import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { withRouter } from 'react-router-dom';

import Auth from '../../services/Auth';
import BitsyApollo from '../../services/BitsyApollo';
import CartUtils from '../../services/CartUtils';
import Environment from '../../services/Environment';
import Features from '../../services/Features';
import Logger from '../../services/Logger';
import checkoutRouting from '../../services/checkoutRouting';
import checkoutVariant from '../../services/checkoutVariant';
import { fetchPremiumUpgradeProduct } from '../../services/premiumUpgrade';
import { readFirstMonthPrices } from '../../services/firstMonthPricing';
import {
  publishCartChange,
  registerCartDrawer,
} from '../../services/cartDrawer';
import { buildTotals } from '../../services/cartTotals';
import { removeSubscription } from '../../services/cartSubscription';
import {
  WINDOWS,
  fetchUpsellCatalogue,
  readWindowsConfig,
  selectUpsellAddOns,
  splitUpsellWindows,
} from '../../services/upsellAddOns';
import {
  CART_DRAWER,
  reportAddOnAdded,
  reportAddOnRemoved,
  reportAddOnViewed,
  reportCheckoutOpened,
  reportUpgradeDeclined,
  reportUpgradeSelected,
} from '../../containers/checkout-unified/analytics';
import CartDrawer from './CartDrawer';
import PreCheckoutOfferModal from './PreCheckoutOfferModal';
import './style.scss';

const CartDrawerContext = createContext(null);

/* Add To Cart used to interrupt with a modal and then, for a subscription,
   navigate straight to checkout. The drawer replaces both: the shopper stays
   where they are, sees what is in the cart, and chooses when to check out.

   A subscription gets the upgrade offer first — that is the one interruption
   worth keeping, and it is the same offer the legacy popup makes.

   Only the unified arm on bump uses any of this. With the gate off, callers
   fall through to the behavior they had. */
/* How many boxes the plan buys up front, or null for month-to-month. The
   premium upgrade is charged per box for all of them at checkout on a prepaid
   plan (cart.lib.js:359-369), so the offer cannot claim a free first month. */
// Legacy's cart list name (review/index.js:101).
const UPSELL_LIST = 'Cart Promo';

const prepaidMonthsOf = (cart) => {
  const months = cart?.subscription?.plan?.months;
  return months && months !== 'INFINITY' ? months : null;
};

// The flag the server picks the upgrade by (cartTotals.js buildLineItems).
const hasPremiumUpgrade = (cart) =>
  (cart?.productsAndDetails || []).some(
    (entry) => entry?.product?.ownPromoData?.isRecurringAddOn,
  );

const confirmationFor = (isRecurringAddOnPromo) =>
  isRecurringAddOnPromo
    ? 'Subscription added to your cart'
    : 'Item added to your cart';

const CartDrawerProviderBase = ({ children, history, onCartUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [cartInfo, setCartInfo] = useState(null);
  const [offer, setOffer] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isReadingCart, setIsReadingCart] = useState(false);
  const [firstMonthPrices, setFirstMonthPrices] = useState({});
  // Everything on offer, read once and chosen from on every cart change.
  const [upsellCatalogue, setUpsellCatalogue] = useState(null);
  const [windowsConfig, setWindowsConfig] = useState(null);
  /* Which one is going in, rather than a second busy flag: the whole row is
     disabled while an add is out, but only the card that was clicked should say
     it is doing something. */
  const [addingUpsellId, setAddingUpsellId] = useState(null);

  const isEnabled = useCallback(() => checkoutRouting.usesCartDrawer(), []);

  /* Read once, not per open: the set changes on a merchandising calendar, not
     on anything the shopper does. Which of them to show is decided per render
     against the cart, which does change. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      /* checkGate answers false until Statsig settles, and this effect runs
         once, so asking before then leaves the drawer with no offers for the
         whole page. ready() settles on timeout too, so a dead Statsig costs
         the upsells rather than hanging on them. */
      await checkoutRouting.ready();
      if (cancelled || !checkoutRouting.usesCartDrawer()) return;

      // One read for every window; the checkout page takes post-purchase from it.
      const catalogue = await fetchUpsellCatalogue();
      if (cancelled) return;
      setUpsellCatalogue(catalogue);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* Signing in reloads Statsig, and a read mid-reload gets the code default, so
     re-read whenever it finishes. Waits for the catalogue so checkout versions
     without the drawer never log an exposure. */
  useEffect(() => {
    if (!upsellCatalogue) return undefined;
    const client = Features.getClient();
    const readConfig = () => {
      if (
        checkoutVariant.variantIsResolved() &&
        checkoutRouting.usesCartDrawer()
      ) {
        setWindowsConfig(readWindowsConfig());
      }
    };
    client.on('values_updated', readConfig);
    readConfig();
    return () => client.off('values_updated', readConfig);
  }, [upsellCatalogue]);

  const upsellWindows = useMemo(
    () =>
      upsellCatalogue && windowsConfig
        ? splitUpsellWindows(upsellCatalogue, windowsConfig)
        : {},
    [upsellCatalogue, windowsConfig],
  );

  const readFirstMonthPrice = useCallback(
    async (cart) => {
      try {
        const found = await readFirstMonthPrices(cart, firstMonthPrices);
        if (found) setFirstMonthPrices((prev) => ({ ...prev, ...found }));
      } catch (error) {
        Logger.info('First-month price unavailable', error);
      }
    },
    [firstMonthPrices],
  );

  const readCart = useCallback(async () => {
    setIsReadingCart(true);
    try {
      if (!Auth.hasToken()) await BitsyApollo.getEmptyUser();
      const cart = await CartUtils.getCart();
      setCartInfo(cart);
      readFirstMonthPrice(cart);
      publishCartChange(cart);
      return cart;
    } catch (error) {
      Logger.info('Cart drawer could not read the cart', error);
      return null;
    } finally {
      setIsReadingCart(false);
    }
  }, [readFirstMonthPrice]);

  const close = useCallback(() => {
    setIsOpen(false);
    setOffer(null);
  }, []);

  /* Which product the offer names. A subscription is offered the premium
     upgrade Statsig points at — the same id checkout-single-page's Start
     Membership adds. There are two premium upgrade products, and the server's
     pre-checkout promo record is the other one, so asking the server for a
     subscription would offer the wrong product.

     Everything else asks the server, as legacy does: a one-time product gets an
     offer too, and isRecurringAddOnPromo only selects which record comes back.
     A subscription whose config names no product falls back there as well,
     rather than losing the offer. */
  const resolveOffer = useCallback(
    async ({ productAddedToCart, isRecurringAddOnPromo }) => {
      if (isRecurringAddOnPromo) {
        const configured = await fetchPremiumUpgradeProduct();
        if (configured) return configured;
      }

      const promos = await BitsyApollo.getPreCheckoutPromoProduct({
        isRecurringAddOnPromo,
        provider: Environment.store,
        productAddedToCart,
      });
      return Array.isArray(promos) && promos.length ? promos[0] : null;
    },
    [],
  );

  /* Called instead of the add-to-cart modal. isRecurringAddOnPromo is carried
     through to the modal as well as to the lookup, because it also decides what
     the copy may claim — a premium upgrade can talk about first boxes and
     monthly prices, a one-time product cannot.

     Whatever comes back is shown ahead of the drawer, and an add with no offer
     configured goes straight to it rather than flashing an empty modal.

     Premium is checked after the add: signed in, a plan add strips it
     (cart.directives.js addItemToCart); a guest's cart keeps it. */
  const openAfterAddToCart = useCallback(
    async ({ productAddedToCart, isRecurringAddOnPromo = false } = {}) => {
      const cart = await readCart();
      if (onCartUpdate) onCartUpdate();

      // Set either way, here and in the catch: this provider never unmounts, so
      // an offer left in place outlives the add that produced it.
      let shown = null;
      try {
        const promo =
          isRecurringAddOnPromo && hasPremiumUpgrade(cart)
            ? null
            : await resolveOffer({ productAddedToCart, isRecurringAddOnPromo });
        if (promo) {
          shown = {
            promo,
            isRecurring: !!isRecurringAddOnPromo,
            prepaidMonths: prepaidMonthsOf(cart),
            confirmation: confirmationFor(isRecurringAddOnPromo),
          };
        }
      } catch (error) {
        Logger.info('Pre-checkout offer unavailable', error);
      }
      setOffer(shown);

      setIsOpen(true);
      /* Nothing to settle, so this add is already the whole moment — the two
         offer exits report it themselves. */
      if (!shown) reportCheckoutOpened(cart);
      return cart;
    },
    [onCartUpdate, readCart, resolveOffer],
  );

  /* Opens on the click, then reads. Waiting for the cart first put a round-trip
     between the cart icon and anything happening, which is the whole of the
     delay — the drawer has to be told to open before it can start sliding.

     The cart it last read is still in state, so a second open shows the real
     contents straight away and refreshes them underneath. A first open has
     nothing yet and says so, rather than claiming the cart is empty. */
  const open = useCallback(() => {
    setIsOpen(true);
    return readCart();
  }, [readCart]);

  /* Klaviyo's abandoned-cart flow triggers on this, and on this arm the cart is
     the drawer — waiting for the checkout page would miss every shopper who
     never reaches it. Reported once the offer is settled rather than on the add
     itself, so an accepted upgrade is in the cart the event describes; the
     checkout page reports again when the shopper gets there. */
  const acceptOffer = useCallback(async () => {
    if (!offer) return;
    // Read before the offer is cleared below; only the upgrade reports.
    const wasUpgrade = offer.isRecurring;
    setIsBusy(true);
    let cart = cartInfo;
    try {
      await CartUtils.addProductToCart(
        offer.promo,
        'NONE',
        {},
        Environment.store,
        true,
        null,
        null,
        null,
        false,
        'Pre-Cart Promo',
      );
      if (wasUpgrade) reportUpgradeSelected();
      cart = await readCart();
      if (onCartUpdate) onCartUpdate();
    } catch (error) {
      Logger.error(error);
    }
    setIsBusy(false);
    setOffer(null);
    reportCheckoutOpened(cart);
  }, [cartInfo, offer, onCartUpdate, readCart]);

  /* cartInfo, not a re-read: declining changes nothing, and it is the cart
     openAfterAddToCart already read for this add. */
  const declineOffer = useCallback(() => {
    if (offer?.isRecurring) reportUpgradeDeclined();
    setOffer(null);
    reportCheckoutOpened(cartInfo);
  }, [cartInfo, offer]);

  /* Adds an upsell from the block in the drawer. promoItem is the one argument
     that carries money: it is what puts the item on the cart at
     ownPromoData.cost rather than its shelf price, and it is what both the
     post-purchase add and the pre-cart offer already pass.

     The other three are deliberately not what the post-purchase add sent.
     fetchCartWithPendingPurchase existed so the add would land on the held
     purchase — there is no hold to land on before checkout. And the two modal
     flags resolve to 'CART SUCCESS' (CartUtils.js:453-467), which is the one
     that does nothing: the drawer is already open and showing the result. */
  const addUpsell = useCallback(
    async (product) => {
      if (!product || isBusy) return;
      setIsBusy(true);
      setAddingUpsellId(product._id);
      reportAddOnViewed(product, 'Cart Review');
      try {
        await CartUtils.addProductToCart(
          product,
          'NONE',
          {},
          Environment.store,
          true,
          null,
          null,
          null,
          false,
          UPSELL_LIST,
        );
        reportAddOnAdded(product, CART_DRAWER);
        await readCart();
        if (onCartUpdate) onCartUpdate();
      } catch (error) {
        Logger.error(error);
      }
      setIsBusy(false);
      setAddingUpsellId(null);
    },
    [isBusy, onCartUpdate, readCart],
  );

  /* The fourth argument is the entry's index, and it was null here. Both
     temp-cart branches in CartUtils are guarded by `index >= 0` (CartUtils.js:
     158 and 216), so without it they wrote the cart back untouched — an
     anonymous shopper, who is on that branch until the account step gives them
     a token, clicked the trash and the row stayed. */
  const removeLine = useCallback(
    async (line) => {
      setIsBusy(true);
      /* Resolved before the delete, because readCart replaces cartInfo. The
         line carries no product id — its `id` is the entry's — so the entry is
         the only way back to one, and the catalogue is what says it was an
         add-on rather than something the shopper chose for themselves. */
      const removed = (cartInfo?.productsAndDetails || [])[line.entryIndex]
        ?.product;
      try {
        if (line.kind === 'sub') {
          await removeSubscription(cartInfo, readCart);
        } else {
          await CartUtils.deleteProductFromCart(
            cartInfo,
            line.id,
            null,
            line.entryIndex,
          );
          const drawerOffers = upsellWindows[WINDOWS.CART_DRAWER] ?? [];
          if (removed && drawerOffers.some((p) => p._id === removed._id)) {
            reportAddOnRemoved(removed, CART_DRAWER);
          }
        }
        await readCart();
        if (onCartUpdate) onCartUpdate();
      } catch (error) {
        Logger.error(error);
      }
      setIsBusy(false);
    },
    [cartInfo, onCartUpdate, readCart, upsellWindows],
  );

  const changeQty = useCallback(
    async (line, next) => {
      const current = line.qty || 1;
      if (next === current) return;
      if (next < 1) {
        await removeLine(line);
        return;
      }

      /* By position, not by id: a temp cart's entries carry no _id at all
         (BitsyApollo.js:438), so every line's id is undefined there and a
         lookup by id matches whichever entry comes first. */
      const entry = (cartInfo?.productsAndDetails || [])[line.entryIndex];
      if (!entry) return;

      setIsBusy(true);
      try {
        await CartUtils.changeQuantityOfProduct(
          entry,
          next > current ? 'plus' : 'minus',
          line.entryIndex,
        );
        await readCart();
        if (onCartUpdate) onCartUpdate();
      } catch (error) {
        Logger.error(error);
      }
      setIsBusy(false);
    },
    [cartInfo, onCartUpdate, readCart, removeLine],
  );

  const goToCheckout = useCallback(() => {
    setIsOpen(false);
    const destination = checkoutRouting.getCheckoutDestination(cartInfo);
    /* Opened from checkout itself, this button has nowhere to go — pushing the
       route again remounts the page and empties the form the shopper filled. */
    if (destination.pathname === history.location.pathname) return;
    history.push(destination);
  }, [cartInfo, history]);

  /* The catalogue rather than the chosen list: the checkout page picks against
     the cart it is about to buy, which is the one the rules have to answer to. */
  const value = useMemo(
    () => ({
      isEnabled,
      open,
      openAfterAddToCart,
      close,
      upsellWindows,
    }),
    [close, isEnabled, open, openAfterAddToCart, upsellWindows],
  );

  /* The Add To Cart callers are class components and cannot read the context,
     so the handle is published for them here. */
  useEffect(() => registerCartDrawer(value), [value]);

  /* Chosen on every render rather than held: the cart is what decides which of
     these are still worth offering, and it changes under the drawer. */
  const upsells = selectUpsellAddOns(
    upsellWindows[WINDOWS.CART_DRAWER] ?? [],
    cartInfo,
  );

  const totals = buildTotals(cartInfo, { firstMonthPrices });
  /* Only a first read has nothing to show; a refresh keeps the contents on
     screen rather than replacing them with placeholders. */
  const isAwaitingCart = isReadingCart && !cartInfo;

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
      <CartDrawer
        isOpen={isOpen && !offer}
        totals={totals}
        isBusy={isBusy}
        isAwaitingCart={isAwaitingCart}
        onClose={close}
        onCheckout={goToCheckout}
        onRemoveLine={removeLine}
        onChangeQty={changeQty}
        upsells={upsells}
        onAddUpsell={addUpsell}
        addingUpsellId={addingUpsellId}
      />
      <PreCheckoutOfferModal
        isOpen={!!offer}
        promo={offer?.promo}
        isRecurring={!!offer?.isRecurring}
        prepaidMonths={offer?.prepaidMonths}
        confirmation={offer?.confirmation}
        isBusy={isBusy}
        onAccept={acceptOffer}
        onDecline={declineOffer}
      />
    </CartDrawerContext.Provider>
  );
};

export const CartDrawerProvider = withRouter(CartDrawerProviderBase);

/* Callers that may run outside the provider get a disabled stub rather than a
   crash — the drawer is bump-only and the legacy arm never mounts it. */
const DISABLED = {
  isEnabled: () => false,
  open: () => {},
  openAfterAddToCart: () => Promise.resolve(null),
  close: () => {},
  upsellWindows: {},
};

// Every upsell window, read once here and shared by the drawer and the checkout page.
export const useUpsellWindows = () =>
  (useContext(CartDrawerContext) || DISABLED).upsellWindows;

export default CartDrawerProvider;
